import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import API from '../../../../services/API'
import moment from 'moment'
import ErrorIcon from '@mui/icons-material/Error'
import { useNavigate } from 'react-router-dom'
import DetailsBox from '../../../../components/Cards/DetailsBox'
import AnDateField from '../../../../components/DateTime/DateField'
import EmployeeSelector from '../../../events/EmployeeSelector'
import { Search } from '@mui/icons-material'
import ListReportPDF from '../../../../components/PDF/ListReportPDF'
import usePrintChrome from '../../../../components/PDF/usePrintChrome'
import { BAD, fmtDate, fmtDateTime, GOOD } from '../../../../components/PDF/pdfKit'
import { downloadPdf } from '../../../../helpers/downloadPdf'

const CompletedVDIs = () => {
  const [vdis, setVdis] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })
  const [rowCount, setRowCount] = useState(0)
  const [searchParams, setSearchParams] = useState({
    dateFrom: moment().subtract(1, 'week'),
    dateTo: moment().endOf('today'),
    vehicle: null,
  })
  const [vehicleOptions, setVehicleOptions] = useState([])
  const navigate = useNavigate()

  const getVDIs = () => {
    setLoading(true)
    API.post('/fleet/completed-vdis', {
      paginationModel,
      searchParams: {
        ...searchParams,
        vehicle: searchParams?.vehicle?._id,
        completedBy: searchParams?.employee?._id,
      },
    })
      .then((res) => {
        if (res.status === 200) {
          setVdis(res.data.vdis)
          setRowCount(res.data.rowCount)
        }
      })
      .catch((e) => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    getVDIs()
  }, [paginationModel])

  useEffect(() => {
    API.get('/vehiclelist').then((res) => {
      if (res.status === 200) {
        setVehicleOptions(res.data)
      }
    })
  }, [])

  // useEffect(() => {
  //   Promise.all([getVDIs(), API.get('/vehiclelist')]).then(
  //     ([vdiRes, vehicleListRes]) => {
  //       if (vehicleListRes.status === 200) {
  //         setVehicleOptions(vehicleListRes.data)
  //       }
  //     },
  //   )
  // }, [])

  const chrome = usePrintChrome()

  // The grid is server-paginated, so printing re-fetches the whole filtered
  // set (the server honours an arbitrary pageSize) rather than one page.
  const handlePrint = async () => {
    const res = await API.post('/fleet/completed-vdis', {
      paginationModel: { page: 0, pageSize: Math.max(rowCount, 1) },
      searchParams: {
        ...searchParams,
        vehicle: searchParams?.vehicle?._id,
        completedBy: searchParams?.employee?._id,
      },
    })
    if (res.status !== 200) throw new Error('Unable to load VDIs for printing')
    const allVdis = res.data.vdis || []
    const withIssues = allVdis.filter((vdi) => vdi.somethingWrong)
    const vehicleCount = new Set(
      allVdis.map((vdi) => vdi.vehicle?._id).filter(Boolean),
    ).size

    const subtitleParts = [
      `${
        searchParams?.dateFrom?.isValid?.()
          ? fmtDate(searchParams.dateFrom)
          : 'Any'
      } - ${searchParams?.dateTo?.isValid?.() ? fmtDate(searchParams.dateTo) : 'Any'}`,
      searchParams?.vehicle ? `Vehicle: ${searchParams.vehicle.callSign}` : null,
      searchParams?.employee
        ? `Completed by: ${
            [
              searchParams.employee.firstName ||
                searchParams.employee.userAccount?.firstName,
              searchParams.employee.lastName ||
                searchParams.employee.userAccount?.lastName,
            ]
              .filter(Boolean)
              .join(' ') || 'Selected employee'
          }`
        : null,
    ].filter(Boolean)

    await downloadPdf(
      <ListReportPDF
        title="Completed VDIs"
        subtitle={subtitleParts.join(' · ')}
        docLabel="Fleet Report"
        {...chrome}
        stats={[
          { label: 'VDIs completed', value: allVdis.length },
          {
            label: 'Issues reported',
            value: withIssues.length,
            color: withIssues.length ? BAD : GOOD,
          },
          { label: 'Vehicles inspected', value: vehicleCount },
        ]}
        columns={[
          { label: 'Completed', flex: 0.8 },
          { label: 'Call Sign', flex: 0.8 },
          { label: 'Completed By', flex: 1.2 },
          { label: 'Issues', flex: 0.8 },
        ]}
        rows={allVdis.map((vdi) => [
          fmtDateTime(vdi.date),
          vdi.vehicle?.callSign ?? 'Removed vehicle',
          `${vdi.completedBy?.firstName ?? ''} ${vdi.completedBy?.lastName ?? ''}`.trim() ||
            '-',
          vdi.somethingWrong
            ? { pill: { label: 'Issues reported', color: BAD } }
            : { pill: { label: 'Clear', color: GOOD } },
        ])}
        emptyText="No VDIs in this period"
      />,
      `AmbuNet_Completed_VDIs_${moment().format('YYYY-MM-DD')}.pdf`,
    )
  }

  return (
    <Stack gap={1}>
      <Typography variant="title2">Completed VDIs</Typography>
      <DetailsBox title="Search/Filter">
        <Box>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} lg={2} minWidth={120}>
              <AnDateField
                label="From"
                value={searchParams?.dateFrom}
                onChange={(newDate) => {
                  setSearchParams((prev) => ({
                    ...prev,
                    dateFrom: newDate,
                  }))
                }}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} lg={2} minWidth={120}>
              <AnDateField
                label="To"
                value={searchParams?.dateTo}
                onChange={(newDate) => {
                  setSearchParams((prev) => ({
                    ...prev,
                    dateTo: newDate,
                  }))
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} lg={3.2} minWidth={200}>
              <Autocomplete
                options={vehicleOptions}
                value={searchParams.vehicle}
                onChange={(e, newValue) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    vehicle: newValue,
                  }))
                }
                renderInput={(params) => (
                  <TextField label="Vehicle" {...params} />
                )}
                getOptionLabel={(option) => option.callSign}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} lg={4.2} minWidth={200}>
              <EmployeeSelector
                value={searchParams?.employee}
                onChange={(_, employee) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    employee,
                  }))
                }
                fullWidth
                label="Completed By"
              />
            </Grid>
            <Grid item xs={12} lg={0.6}>
              <Box
                width="100%"
                height="100%"
                alignItems="center"
                justifyContent="center"
                display="flex"
              >
                <IconButton
                  onClick={() => getVDIs()}
                  sx={{
                    borderRadius: 1,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'contrast.main',
                    color: 'white',
                  }}
                >
                  <Search />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DetailsBox>
      <CustomDataGrid
        rows={vdis}
        rowCount={rowCount}
        pageSizeOptions={[10, 25, 50, 100]}
        hideFilters
        onPrint={handlePrint}
        pagination
        paginationMode="server"
        loading={loading}
        norowsoverlay={
          <Alert severity="info">
            No VDIs recorded yet. Crews complete Vehicle Daily Inspections from
            the VDI form (or Start of Shift check) against a registered
            vehicle; completed inspections then appear here. The VDI question
            set is managed in Site Configuration → Fleet → VDI.
          </Alert>
        }
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        columns={[
          {
            field: 'date',
            headerName: 'Completed',
            renderCell: ({ value }) => moment(value).format('DD/MM/YYYY HH:mm'),
            minWidth: 150,
            flex: 0.5,
          },
          {
            field: 'callSign',
            headerName: 'Call Sign',
            valueGetter: (value, row) => row.vehicle?.callSign,
            flex: 0.75,
          },
          {
            field: 'completedBy',
            headerName: 'Completed By',
            valueGetter: (value, row) => row.completedBy._id,
            renderCell: ({ row }) =>
              `${row.completedBy.firstName} ${row.completedBy.lastName}`,
            minWidth: 200,
            flex: 1,
          },
          {
            field: 'somethingWrong',
            headerName: 'Issues',
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ value }) =>
              value ? (
                <ErrorIcon color="error" sx={{ textAlign: 'center' }} />
              ) : null,
            minWidth: 150,
            flex: 0.25,
          },
        ]}
        getRowId={(params) => params._id}
        onRowClick={(params) => window.open('/vdis/' + params.row._id)}
        disableRowSelectionOnClick
        sx={{
          '.MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
        }}
      />
    </Stack>
  )
}

export default CompletedVDIs
